import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/stores/auth/auth-store";
import { usePermissions } from "@/hooks/permissions/usePermissions";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { Project, ProjectPermission } from "@/types";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Lock, AlertTriangle } from "lucide-react";
import {
  getViewerPermissionIdString,
  getViewerPermissionIdStrings,
} from "./viewerPermissionIds";

interface ViewerPermissionGuardProps {
  project: Project | null;
  children: React.ReactNode;
  onAccessDenied?: () => void;
}

interface AccessInfo {
  hasAccess: boolean;
  accessType: "owner" | "member" | "public" | "shared_link" | "denied";
  permissions: ProjectPermission[];
  canEdit: boolean;
  canComment: boolean;
  canShare: boolean;
  restrictionMessage?: string;
}

const OWNER_ACCESS_PERMISSIONS: ProjectPermission[] = [
  ProjectPermission.READ,
  ProjectPermission.WRITE,
  ProjectPermission.ADMIN,
  ProjectPermission.SHARE,
];

const EDIT_MEMBER_ACCESS_PERMISSIONS: ProjectPermission[] = [
  ProjectPermission.READ,
  ProjectPermission.WRITE,
];

const READ_ONLY_ACCESS_PERMISSIONS: ProjectPermission[] = [ProjectPermission.READ];

export const ViewerPermissionGuard: React.FC<ViewerPermissionGuardProps> = ({
  project,
  children,
  onAccessDenied,
}) => {
  const { user } = useAuth();
  const { loading: isLoading } = usePermissions();
  const { organizations } = useOrganizationStore();
  const currentUserIds = useMemo(
    () => getViewerPermissionIdStrings(user?.id, user?._id),
    [user?.id, user?._id],
  );
  const [accessInfo, setAccessInfo] = useState<AccessInfo | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        setIsChecking(false);
        setAccessInfo({
          hasAccess: false,
          accessType: "denied",
          permissions: [],
          canEdit: false,
          canComment: false,
          canShare: false,
          restrictionMessage: "Authentication required",
        });
        return;
      }

      // Don't proceed with access check if project is not loaded yet or has invalid data
      if (!project || !project.id) {
        setIsChecking(true);
        return;
      }

      setIsChecking(true);

      try {
        if (currentUserIds.size === 0) {
          setAccessInfo({
            hasAccess: false,
            accessType: "denied",
            permissions: [],
            canEdit: false,
            canComment: false,
            canShare: false,
            restrictionMessage: "Authentication required",
          });
          setIsChecking(false);
          return;
        }

        const projectOwnerId = getViewerPermissionIdString(project.ownerId);
        const scopedOwnerId = getViewerPermissionIdString(project.owner_id);
        const isDirectOwner =
          (projectOwnerId !== undefined && currentUserIds.has(projectOwnerId)) ||
          (project.owner_type === "User" &&
            scopedOwnerId !== undefined &&
            currentUserIds.has(scopedOwnerId));

        // Check if user is direct project owner (legacy field)
        if (isDirectOwner) {
          setAccessInfo({
            hasAccess: true,
            accessType: "owner",
            permissions: OWNER_ACCESS_PERMISSIONS,
            canEdit: true,
            canComment: true,
            canShare: true,
          });
          setIsChecking(false);
          return;
        }

        // Check if project is owned by an organization the user belongs to
        if (project.owner_type === "Organization" && scopedOwnerId) {
          const userOrg = organizations.find(
            (org) => getViewerPermissionIdString(org._id) === scopedOwnerId
          );
          if (userOrg) {
            // User is a member of the owning organization
            const orgRole = userOrg.membership?.role;
            const canEdit = orgRole === "owner" || orgRole === "admin" || orgRole === "member";
            const canAdmin = orgRole === "owner" || orgRole === "admin";

            setAccessInfo({
              hasAccess: true,
              accessType: canAdmin ? "owner" : "member",
              permissions: canAdmin
                ? OWNER_ACCESS_PERMISSIONS
                : canEdit
                  ? EDIT_MEMBER_ACCESS_PERMISSIONS
                  : READ_ONLY_ACCESS_PERMISSIONS,
              canEdit,
              canComment: true,
              canShare: canAdmin,
            });
            setIsChecking(false);
            return;
          }
        }

        // Check project membership
        const membership = project.members?.find(
          (m) => {
            const memberUserId = getViewerPermissionIdString(m.userId);
            return (
              memberUserId !== undefined &&
              currentUserIds.has(memberUserId) &&
              m.status === "active"
            );
          },
        );
        if (membership) {
          const hasWrite = membership.permissions.includes(ProjectPermission.WRITE);
          const hasAdmin = membership.permissions.includes(ProjectPermission.ADMIN);
          const hasShare = membership.permissions.includes(ProjectPermission.SHARE);

          setAccessInfo({
            hasAccess: true,
            accessType: "member",
            permissions: membership.permissions,
            canEdit: hasWrite || hasAdmin,
            canComment: true, // All members can comment
            canShare: hasShare || hasAdmin,
          });
          setIsChecking(false);
          return;
        }

        // Check public visibility
        if (project.visibility === "public") {
          setAccessInfo({
            hasAccess: true,
            accessType: "public",
            permissions: READ_ONLY_ACCESS_PERMISSIONS,
            canEdit: false,
            canComment: false,
            canShare: false,
          });
          setIsChecking(false);
          return;
        }

        // Access denied
        setAccessInfo({
          hasAccess: false,
          accessType: "denied",
          permissions: [],
          canEdit: false,
          canComment: false,
          canShare: false,
          restrictionMessage: "Project is private and you are not a member",
        });
      } catch (error) {
        console.error("Error checking project permissions:", error);
        setAccessInfo({
          hasAccess: false,
          accessType: "denied",
          permissions: [],
          canEdit: false,
          canComment: false,
          canShare: false,
          restrictionMessage: "Failed to verify permissions",
        });
      }

      setIsChecking(false);
    };

    checkAccess();
  }, [currentUserIds, organizations, project, user]);

  // Loading state
  if (isChecking || isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <div className="text-muted-foreground">Checking permissions...</div>
        </div>
      </div>
    );
  }

  // Access denied - only call onAccessDenied if we've actually checked and denied access
  if (accessInfo && !accessInfo.hasAccess) {
    onAccessDenied?.();

    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="rounded-full bg-destructive/10 p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Lock className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-4">
            {accessInfo?.restrictionMessage ||
              "You don't have permission to view this project."}
          </p>
          <div className="flex gap-2 justify-center">
            <Button onClick={() => window.history.back()}>Go Back</Button>
            {!getViewerPermissionIdStrings(user?.id, user?._id).has(
              getViewerPermissionIdString(project?.ownerId) ?? "",
            ) && (
              <Button
                variant="outline"
                onClick={() => {
                  // TODO: Implement request access functionality
                  console.log("Request access to project");
                }}
              >
                Request Access
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Still checking permissions - show loading state
  if (!accessInfo) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <div className="text-muted-foreground">Verifying access...</div>
        </div>
      </div>
    );
  }

  // Access granted - render children without access indicator
  return (
    <div className="relative h-full">
      {/* Restriction message */}
      {accessInfo.restrictionMessage && (
        <div className="absolute top-2 right-2 z-40">
          <Alert className="max-w-sm">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription className="text-xs">
              {accessInfo.restrictionMessage}
            </AlertDescription>
          </Alert>
        </div>
      )}

      {children}
    </div>
  );
};

// Hook to access current permission info
export const useViewerPermissions = () => {
  const [permissionInfo, setPermissionInfo] = useState<AccessInfo | null>(null);

  const setPermissions = (info: AccessInfo) => {
    setPermissionInfo(info);
  };

  return {
    permissionInfo,
    setPermissions,
    hasEditAccess: permissionInfo?.canEdit || false,
    hasCommentAccess: permissionInfo?.canComment || false,
    hasShareAccess: permissionInfo?.canShare || false,
    isOwner: permissionInfo?.accessType === "owner",
    isMember: permissionInfo?.accessType === "member",
    isPublicView: permissionInfo?.accessType === "public",
    isSharedLink: permissionInfo?.accessType === "shared_link",
  };
};
